<template>
  <main class="messages-shell">
    <section class="panel inbox-header">
      <div class="section-heading">
        <div>
          <h1>Graduate messages</h1>
          <p class="muted">Read graduate replies, answer them, and mark conversations handled.</p>
        </div>
        <button class="secondary-button" type="button" :disabled="loading || !universitySlug" @click="refreshInbox">Refresh</button>
      </div>
      <p v-if="error" class="notice error" role="alert">{{ error }}</p>
      <p v-if="initialLoading" class="empty-copy">Loading messages…</p>
      <p v-else-if="!universities.length" class="empty-copy">No university inboxes are available for your account.</p>
      <template v-else>
        <div class="inbox-filters">
          <div class="filter-field">
            <label for="messages-university">University</label>
            <select id="messages-university" v-model="universitySlug" @change="changeUniversity">
              <option v-for="university in universities" :key="university.slug" :value="university.slug">{{ university.name || university.slug }}</option>
            </select>
          </div>
          <div class="filter-field">
            <label for="messages-ceremony">Ceremony</label>
            <select id="messages-ceremony" v-model="eventId" @change="changeFilters">
              <option value="">All ceremonies</option>
              <option v-for="event in events" :key="event.eventId" :value="event.eventId">{{ event.eventName }}{{ event.silenced ? ' · Silenced' : '' }}</option>
            </select>
          </div>
          <label class="checkbox-label"><input v-model="showSilenced" type="checkbox" @change="changeFilters" /> Show silenced</label>
          <span class="inbox-count">{{ unreadCount }} unread {{ unreadCount === 1 ? 'conversation' : 'conversations' }}</span>
        </div>
        <div v-if="selectedEvent" class="ceremony-setting">
          <p>{{ selectedEvent.silenced ? 'This ceremony is silenced. Replies are still saved, but excluded from the normal inbox and unread count.' : 'Messages from this ceremony appear in the inbox and unread count.' }}</p>
          <button class="secondary-button" type="button" :disabled="settingBusy" @click="toggleSilence(selectedEvent)">{{ selectedEvent.silenced ? 'Unmute ceremony' : 'Silence ceremony' }}</button>
        </div>
        <div class="rollout-setting" :class="{ disabled: !enabled }">
          <p v-if="enabled">Your university is responsible for checking this inbox and answering graduate messages.</p>
          <p v-else>Graduate message routing is not enabled for this university. Existing conversations remain available. Contact MGC to arrange activation.</p>
          <button v-if="isInternalAdmin" class="secondary-button" type="button" :disabled="settingBusy" @click="toggleEnabled">{{ enabled ? 'Disable message routing' : 'Enable message routing' }}</button>
        </div>
      </template>
    </section>

    <div v-if="universitySlug" class="inbox-layout" :class="{ 'has-conversation': conversation }">
      <section class="panel conversation-list" aria-label="Conversations">
        <div class="list-heading"><h2>Conversations</h2><span class="muted">{{ conversations.length }} loaded</span></div>
        <p v-if="loading && !conversations.length" class="empty-copy">Loading conversations…</p>
        <p v-else-if="!conversations.length" class="empty-copy">No graduate replies in this view.</p>
        <button v-for="item in conversations" :key="item.id" type="button" class="conversation-row" :class="{ selected: conversation?.id === item.id, unread: item.unread }" :aria-pressed="conversation?.id === item.id" @click="openConversation(item.id)">
          <span class="row-heading"><strong>{{ item.graduateName || 'Graduate' }}</strong><span v-if="item.unread" class="state-badge new">Unread</span></span>
          <span class="row-event">{{ item.eventName }}</span>
          <span class="row-preview">{{ item.lastMessagePreview || 'No message preview' }}</span>
          <span class="row-footer"><time :datetime="item.lastMessageAt">{{ formatDate(item.lastMessageAt) }}</time><span>{{ item.handled ? 'Handled' : 'Needs attention' }}</span></span>
        </button>
        <button v-if="nextCursor" class="secondary-button load-more" type="button" :disabled="loading" @click="loadInbox(true)">Load more conversations</button>
      </section>

      <section class="panel conversation-detail" aria-label="Conversation">
        <p v-if="detailLoading" class="empty-copy">Loading conversation…</p>
        <template v-else-if="conversation">
          <button class="mobile-back secondary-button" type="button" @click="closeConversation">Back to conversations</button>
          <div class="detail-heading">
            <div>
              <h2>{{ conversation.graduateName || 'Graduate' }}</h2>
              <p class="muted graduate-email">{{ conversation.graduateEmail }}</p>
              <p class="context-copy">{{ conversation.eventName }} <span v-if="conversation.eventArchived || conversation.eventStatus === 'archived'" class="state-badge">Archived</span></p>
            </div>
            <span class="state-badge" :class="{ new: !conversation.handled }">{{ conversation.handled ? 'Handled' : 'Needs attention' }}</span>
          </div>
          <div v-if="conversationEvent?.silenced" class="notice">This ceremony is silenced. New replies stay out of the normal inbox and unread count.</div>
          <div v-if="hasNewMessages" class="notice new-message" role="status">
            <p>This conversation has been updated. Read the latest before marking it handled.</p>
            <button class="secondary-button" type="button" @click="openConversation(conversation.id)">Show latest messages</button>
          </div>
          <p v-if="detailNotice" class="notice" role="status">{{ detailNotice }}</p>
          <p v-if="detailError" class="notice error" role="alert">{{ detailError }}</p>
          <div class="message-thread">
            <article v-for="message in messages" :key="message.id" class="message" :class="message.direction">
              <div class="message-heading"><strong>{{ message.direction === 'inbound' ? 'Incoming email' : 'University reply' }}</strong><time :datetime="message.createdAt">{{ formatDate(message.createdAt) }}</time></div>
              <p class="message-sender">From: {{ message.senderEmail || 'Sender unavailable' }}</p>
              <p class="message-body">{{ message.body || '(No text content)' }}</p>
              <p v-if="message.hasAttachments" class="attachment-notice">Attachment unavailable</p>
              <p v-if="message.direction === 'outbound'" class="delivery-status">{{ deliveryStatus(message.status) }}</p>
            </article>
          </div>
          <form class="reply-form" @submit.prevent="sendReply">
            <label for="message-reply">Reply to {{ conversation.graduateName || 'graduate' }}</label>
            <textarea id="message-reply" v-model="replyDraft" rows="5" maxlength="20000" :disabled="Boolean(pendingReply) || Boolean(actionBusy)" placeholder="Write a reply…"></textarea>
            <p v-if="pendingReply && !actionBusy" class="muted">Delivery could not be confirmed. Retry this same reply to avoid sending it twice.</p>
            <div class="reply-actions">
              <button class="primary-button" type="submit" :disabled="actionBusy || !replyDraft.trim()">{{ actionBusy === 'reply' ? 'Sending…' : pendingReply ? 'Retry reply' : 'Send reply' }}</button>
              <button class="secondary-button" type="button" :disabled="Boolean(actionBusy) || conversation.handled || hasNewMessages" @click="markHandled">{{ actionBusy === 'handled' ? 'Saving…' : 'Mark handled' }}</button>
              <button v-if="conversationEvent && !selectedEvent" class="text-button" type="button" :disabled="settingBusy" @click="toggleSilence(conversationEvent)">{{ conversationEvent.silenced ? 'Unmute ceremony' : 'Silence ceremony' }}</button>
            </div>
          </form>
        </template>
        <p v-else-if="detailError" class="notice error" role="alert">{{ detailError }}</p>
        <p v-else class="empty-copy detail-empty">Choose a conversation to read and reply.</p>
      </section>
    </div>
  </main>
</template>

<script>
import {
  fetchConversation, fetchEvent, fetchMessages, fetchMessageUniversities,
  markConversationHandled, markConversationRead, replyToConversation,
  setCeremonySilence, setUniversityMessagesEnabled,
} from "@/lib/api";
import { getSessionToken, isGlobalInternalAdmin } from "@/lib/auth";

function readPendingReplies() {
  try {
    return JSON.parse(window.sessionStorage.getItem(`orator-pending-replies:${getSessionToken()}`) || "{}");
  } catch (error) {
    return {};
  }
}

export default {
  name: "MessagesView",
  data() {
    return {
      universities: [], universitySlug: "", events: [], eventId: "", showSilenced: false,
      conversations: [], unreadCount: 0, enabled: false, nextCursor: "",
      conversation: null, messages: [], drafts: {}, pendingReplies: readPendingReplies(),
      initialLoading: true, loading: false, detailLoading: false, actionBusy: "", settingBusy: false,
      error: "", detailError: "", detailNotice: "", hasNewMessages: false,
      listRequest: 0, detailRequest: 0, pollTimer: null,
    };
  },
  computed: {
    isInternalAdmin() { return isGlobalInternalAdmin(); },
    selectedEvent() { return this.events.find(event => event.eventId === this.eventId); },
    conversationEvent() { return this.events.find(event => event.eventId === this.conversation?.eventId); },
    pendingReply() { return this.pendingReplies[this.conversation?.id]; },
    replyDraft: {
      get() { return this.pendingReply?.body || this.drafts[this.conversation?.id] || ""; },
      set(value) { if (this.conversation) this.drafts[this.conversation.id] = value; },
    },
  },
  watch: {
    "$route.query"(query) {
      const universitySlug = String(query.universitySlug || this.universitySlug);
      const eventId = String(query.eventId || "");
      const showSilenced = query.showSilenced === "true";
      if (universitySlug === this.universitySlug && eventId === this.eventId && showSilenced === this.showSilenced) return;
      if (!this.universities.some(university => university.slug === universitySlug)) return;
      this.universitySlug = universitySlug;
      this.eventId = eventId;
      this.showSilenced = showSilenced;
      this.closeConversation();
      this.loadInbox();
    },
  },
  async mounted() {
    try {
      const response = await fetchMessageUniversities();
      this.universities = response.universities || [];
      let requestedSlug = String(this.$route.query.universitySlug || "");
      this.eventId = String(this.$route.query.eventId || "");
      this.showSilenced = this.$route.query.showSilenced === "true";
      if (this.eventId && !requestedSlug) {
        const snapshot = await fetchEvent(this.eventId);
        requestedSlug = snapshot.event?.universitySlug || "";
      }
      this.universitySlug = this.universities.find(university => university.slug === requestedSlug)?.slug || this.universities[0]?.slug || "";
      if (this.universitySlug) await this.loadInbox();
    } catch (error) {
      this.error = this.errorMessage(error, "Unable to load university inboxes.");
    } finally {
      this.initialLoading = false;
    }
    this.pollTimer = window.setInterval(() => {
      if (!document.hidden && !this.loading && !this.settingBusy && this.universitySlug) this.loadInbox();
    }, 30000);
  },
  beforeUnmount() {
    window.clearInterval(this.pollTimer);
    this.listRequest += 1;
    this.detailRequest += 1;
  },
  methods: {
    errorMessage(error, fallback) {
      const message = error?.response?.data?.error;
      return typeof message === "string" ? message : fallback;
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
    },
    deliveryStatus(status) {
      return ({ sent: "Sent", delivered: "Delivered", pending: "Pending delivery", queued: "Queued for delivery", sending: "Sending", failed: "Delivery failed", uncertain: "Delivery needs checking", unknown: "Delivery needs checking" })[status] || "Pending delivery";
    },
    updateHeaderCount() {
      window.dispatchEvent(new Event("orator-messages-updated"));
    },
    updateQuery() {
      this.$router.replace({ path: "/manage/messages", query: {
        universitySlug: this.universitySlug,
        ...(this.eventId ? { eventId: this.eventId } : {}),
        ...(this.showSilenced ? { showSilenced: "true" } : {}),
      } });
    },
    closeConversation() {
      this.detailRequest += 1;
      this.conversation = null;
      this.messages = [];
      this.detailLoading = false;
      this.detailError = "";
      this.detailNotice = "";
      this.hasNewMessages = false;
    },
    async changeUniversity() {
      this.eventId = "";
      this.events = [];
      this.closeConversation();
      this.conversations = [];
      this.updateQuery();
      await this.loadInbox();
    },
    async changeFilters() {
      this.closeConversation();
      this.updateQuery();
      await this.loadInbox();
    },
    async refreshInbox() {
      const conversationId = this.conversation?.id;
      await this.loadInbox();
      if (conversationId && this.conversation?.id === conversationId) await this.openConversation(conversationId, false);
      this.updateHeaderCount();
    },
    async loadInbox(append = false) {
      const request = ++this.listRequest;
      this.loading = true;
      this.error = "";
      try {
        const response = await fetchMessages({
          universitySlug: this.universitySlug,
          ...(this.eventId ? { eventId: this.eventId } : {}),
          showSilenced: this.showSilenced,
          ...(append && this.nextCursor ? { cursor: this.nextCursor } : {}),
        });
        if (request !== this.listRequest) return;
        const incoming = response.conversations || [];
        this.conversations = append ? [...this.conversations, ...incoming.filter(item => !this.conversations.some(existing => existing.id === item.id))] : incoming;
        this.events = response.events || [];
        this.enabled = Boolean(response.enabled);
        this.unreadCount = Number(response.unreadCount) || 0;
        this.nextCursor = response.nextCursor || "";
        const updated = incoming.find(item => item.id === this.conversation?.id);
        if (updated && updated.revision > this.conversation.revision) this.hasNewMessages = true;
        if (this.conversationEvent?.silenced && !this.showSilenced) this.closeConversation();
        this.updateHeaderCount();
      } catch (error) {
        if (request === this.listRequest) this.error = this.errorMessage(error, "Unable to load messages. Try Refresh.");
      } finally {
        if (request === this.listRequest) this.loading = false;
      }
    },
    async openConversation(id, markRead = true) {
      const request = ++this.detailRequest;
      if (this.conversation?.id !== id) {
        this.conversation = null;
        this.messages = [];
      }
      this.detailLoading = true;
      this.detailError = "";
      this.detailNotice = "";
      this.hasNewMessages = false;
      try {
        const response = await fetchConversation(id);
        if (request !== this.detailRequest) return;
        this.conversation = response.conversation;
        this.messages = response.messages || [];
        if (markRead && this.conversation.unread) {
          try {
            const readResponse = await markConversationRead(id, this.conversation.revision);
            if (request !== this.detailRequest) return;
            this.conversation = readResponse.conversation;
          } catch (error) {
            if (request !== this.detailRequest) return;
            if (error?.response?.status === 409) {
              const latest = await fetchConversation(id);
              if (request !== this.detailRequest) return;
              this.conversation = latest.conversation;
              this.messages = latest.messages || [];
              this.detailNotice = "A new message arrived while opening this conversation. It still needs attention.";
            } else {
              this.detailError = this.errorMessage(error, "Unable to mark this conversation read.");
            }
          }
          await this.loadInbox();
          this.updateHeaderCount();
        }
      } catch (error) {
        if (request === this.detailRequest) this.detailError = this.errorMessage(error, "Unable to load this conversation.");
      } finally {
        if (request === this.detailRequest) this.detailLoading = false;
      }
    },
    async markHandled() {
      const { id, revision } = this.conversation;
      this.actionBusy = "handled";
      this.detailError = "";
      this.detailNotice = "";
      try {
        await markConversationHandled(id, revision);
        if (this.conversation?.id === id) {
          await this.openConversation(id, false);
          this.detailNotice = "Conversation marked handled.";
        }
        await this.loadInbox();
        this.updateHeaderCount();
      } catch (error) {
        if (this.conversation?.id !== id) return;
        if (error?.response?.status === 409) {
          await this.openConversation(id, false);
          await this.loadInbox();
          this.detailNotice = "A new message arrived. Review it before marking this conversation handled.";
        } else {
          this.detailError = this.errorMessage(error, "Unable to mark this conversation handled.");
        }
      } finally {
        this.actionBusy = "";
      }
    },
    savePendingReplies() {
      try {
        window.sessionStorage.setItem(`orator-pending-replies:${getSessionToken()}`, JSON.stringify(this.pendingReplies));
      } catch (error) {
        // Keep the retry identity in memory if browser storage is unavailable.
      }
    },
    async sendReply() {
      const id = this.conversation.id;
      const body = this.replyDraft.trim();
      if (!body || this.actionBusy) return;
      if (!this.pendingReplies[id]) {
        this.pendingReplies[id] = { body, clientRequestId: crypto.randomUUID() };
        this.savePendingReplies();
      }
      const attempt = this.pendingReplies[id];
      this.actionBusy = "reply";
      this.detailError = "";
      this.detailNotice = "";
      try {
        await replyToConversation(id, attempt.body, attempt.clientRequestId);
        delete this.pendingReplies[id];
        delete this.drafts[id];
        this.savePendingReplies();
        if (this.conversation?.id === id) {
          await this.openConversation(id, false);
          this.detailNotice = "Reply queued for delivery.";
        }
        await this.loadInbox();
        this.updateHeaderCount();
      } catch (error) {
        if (error?.response?.status >= 400 && error.response.status < 500) {
          this.drafts[id] = attempt.body;
          delete this.pendingReplies[id];
          this.savePendingReplies();
        }
        if (this.conversation?.id === id) this.detailError = this.errorMessage(error, "Unable to confirm this reply. Retry using the same reply.");
      } finally {
        this.actionBusy = "";
      }
    },
    async toggleSilence(event) {
      this.settingBusy = true;
      this.error = "";
      try {
        await setCeremonySilence(event.eventId, !event.silenced);
        await this.loadInbox();
        this.updateHeaderCount();
      } catch (error) {
        this.error = this.errorMessage(error, "Unable to change ceremony silence.");
      } finally {
        this.settingBusy = false;
      }
    },
    async toggleEnabled() {
      this.settingBusy = true;
      this.error = "";
      try {
        await setUniversityMessagesEnabled(this.universitySlug, !this.enabled);
        await this.loadInbox();
        this.updateHeaderCount();
      } catch (error) {
        this.error = this.errorMessage(error, "Unable to change graduate message routing.");
      } finally {
        this.settingBusy = false;
      }
    },
  },
};
</script>

<style scoped>
.messages-shell { max-width: 1380px; margin: 0 auto; padding: 0 1rem 2.5rem; }
.panel { background: white; border: 1px solid var(--orator-smoke); border-radius: 0.75rem; box-shadow: var(--orator-shadow); padding: 1.5rem; }
h1 { font-size: 1.8rem; font-weight: 900; }
h2 { font-size: 1.2rem; font-weight: 800; }
.muted, .empty-copy { color: var(--orator-graphite); font-size: 0.9rem; }
.section-heading, .detail-heading, .list-heading, .row-heading, .row-footer, .message-heading { display: flex; justify-content: space-between; gap: 0.75rem; align-items: flex-start; }
.inbox-filters { display: flex; flex-wrap: wrap; gap: 1rem; align-items: flex-end; margin-top: 1.4rem; }
.filter-field { flex: 1 1 220px; }
label { display: block; font-size: 0.875rem; font-weight: 700; margin-bottom: 0.45rem; }
select, textarea { width: 100%; border: 1px solid var(--orator-steel); border-radius: 0.45rem; padding: 0.65rem 0.75rem; background: white; color: var(--orator-navy); }
textarea { resize: vertical; min-height: 120px; }
.checkbox-label { display: flex; align-items: center; gap: 0.5rem; margin: 0 0 0.65rem; white-space: nowrap; }
.inbox-count { padding-bottom: 0.65rem; font-size: 0.875rem; font-weight: 700; white-space: nowrap; }
.rollout-setting, .ceremony-setting { display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-top: 1rem; padding: 0.85rem 1rem; background: var(--orator-cloud); border-radius: 0.5rem; font-size: 0.85rem; }
.rollout-setting.disabled { background: #fff7ed; }
.primary-button, .secondary-button { display: inline-flex; justify-content: center; align-items: center; padding: 0.6rem 0.9rem; border: 1px solid var(--orator-steel); border-radius: 0.45rem; font-weight: 700; font-size: 0.875rem; white-space: nowrap; }
.primary-button { background: var(--orator-blue); border-color: var(--orator-blue); color: white; }
.secondary-button { background: white; color: var(--orator-arsenic); }
button:disabled, textarea:disabled { cursor: not-allowed; opacity: 0.65; }
button:not(:disabled) { cursor: pointer; }
button:focus-visible, select:focus-visible, textarea:focus-visible { outline: 3px solid var(--orator-blue); outline-offset: 3px; }
.inbox-layout { display: grid; grid-template-columns: minmax(280px, 360px) minmax(0, 1fr); gap: 1.25rem; margin-top: 1.25rem; align-items: start; }
.conversation-list { padding: 0; overflow: hidden; }
.list-heading { padding: 1rem; align-items: center; border-bottom: 1px solid var(--orator-smoke); }
.list-heading h2 { font-size: 1rem; }
.list-heading .muted { font-size: 0.75rem; }
.conversation-row { display: block; text-align: left; width: 100%; border-bottom: 1px solid var(--orator-smoke); padding: 1rem; color: var(--orator-navy); }
.conversation-row:hover { background: #f7f8fb; }
.conversation-row.selected { background: var(--orator-cloud); box-shadow: inset 3px 0 var(--orator-blue); }
.row-heading { align-items: center; }
.row-event, .row-preview { display: block; margin-top: 0.35rem; font-size: 0.85rem; overflow-wrap: anywhere; }
.row-event { color: var(--orator-graphite); }
.row-preview { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.row-footer { margin-top: 0.65rem; font-size: 0.7rem; color: var(--orator-graphite); }
.state-badge { display: inline-flex; white-space: nowrap; align-items: center; padding: 0.2rem 0.45rem; border-radius: 0.35rem; font-size: 0.7rem; font-weight: 700; background: var(--orator-cloud); color: var(--orator-arsenic); }
.state-badge.new { background: #e8f1ff; color: #1453ac; }
.graduate-email, .message-sender { overflow-wrap: anywhere; }
.context-copy { margin-top: 0.5rem; font-size: 0.9rem; }
.context-copy .state-badge { margin-left: 0.4rem; }
.message-thread { margin-top: 1.5rem; display: flex; flex-direction: column; gap: 1rem; }
.message { border: 1px solid var(--orator-smoke); border-radius: 0.6rem; padding: 1rem; }
.message.outbound { background: #f7f9fd; }
.message-heading { align-items: center; font-size: 0.8rem; flex-wrap: wrap; }
.message-heading time { color: var(--orator-graphite); font-size: 0.75rem; }
.message-sender { font-size: 0.8rem; color: var(--orator-graphite); margin-top: 0.35rem; }
.message-body { margin-top: 0.85rem; white-space: pre-wrap; overflow-wrap: anywhere; font-size: 0.9rem; line-height: 1.65; }
.attachment-notice, .delivery-status { margin-top: 0.65rem; color: var(--orator-graphite); font-size: 0.75rem; }
.reply-form { margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid var(--orator-smoke); }
.reply-form .muted { margin-top: 0.5rem; }
.reply-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 0.65rem; margin-top: 0.75rem; }
.text-button { color: var(--orator-graphite); text-decoration: underline; font-size: 0.8rem; padding: 0.4rem; }
.notice { margin-top: 1rem; padding: 0.85rem 1rem; border-radius: 0.5rem; background: var(--orator-cloud); font-size: 0.875rem; }
.notice.error { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
.new-message { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; justify-content: space-between; }
.empty-copy { padding: 1rem 0; }
.conversation-list .empty-copy { padding: 1.25rem; }
.detail-empty { text-align: center; padding: 4rem 1rem; }
.load-more { margin: 1rem; }
.mobile-back { display: none; }
@media (max-width: 800px) {
  .inbox-layout { grid-template-columns: 1fr; }
  .inbox-layout .conversation-detail { display: none; }
  .inbox-layout.has-conversation .conversation-list { display: none; }
  .inbox-layout.has-conversation .conversation-detail { display: block; }
  .mobile-back { display: inline-flex; margin-bottom: 1.25rem; }
}
@media (max-width: 520px) {
  .panel { padding: 1rem; }
  .conversation-list { padding: 0; }
  h1 { font-size: 1.5rem; }
  .inbox-filters { gap: 0.75rem; }
  .checkbox-label { white-space: normal; }
  .rollout-setting, .ceremony-setting { align-items: flex-start; flex-direction: column; }
  .detail-heading { flex-wrap: wrap; }
}
</style>
